import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../Opportunities/DashboardOpportunities.module.css";
import TaskCard from '../../../Components/TaskCard/TaskCard';
import { Card, CardContent, Typography, Grid, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from '../../../redux/store';
import { fetchPersonalLinks } from '../../../redux/slices/personalLinks';
import TwoWeekCalendar from '../../../Components/ResourceCalander/TwoWeekCalendar';
import TriageBookingModal from '../../../Components/TriageBookingModal/TriageBookingModal';
import ITForm from '../../../Components/ITForms/ITForm';
import TownhallForm from '../../../Components/Townhall/TownhallForm';
import TdgForm from '../../../Components/Townhall/TdgForm';
import StandardsAndTemplatesForm from '../../../Components/StandardsForms/StandardsAndTemplatesForm';
import { useMsal } from "@azure/msal-react";
import { fetchDashboardCalendarTwoWeeks, fetchDashboardPersonalTasks } from '../../../redux/slices/dashboardPersonal';

function Personal() {
  const dispatch = useDispatch();
  const personalLinksState = useSelector((s) => s.personalLinks);
  const dashboardPersonal = useSelector((s) => s.dashboardPersonal);
  const { accounts } = useMsal();
  const account = accounts[0];

  const [showGenericTriage, setShowGenericTriage] = useState(false);
  const [showITForm, setShowITForm] = useState(false);
  const [showTownhall, setShowTownhall] = useState(false);
  const [showTdg, setShowTdg] = useState(false);
  const [showStandardsForm, setShowStandardsForm] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const userEmail = account?.username || '';

  const { overdue, thisWeek, nextWeek } = useMemo(() => {
    const jsonData = dashboardPersonal?.tasks || {};

    const parseDate = (dateString) => {
      if (!dateString) return new Date(0);
      const parts = dateString.split('/');
      return new Date(parts[2], parts[1] - 1, parts[0]);
    };

    const mapForCard = (list) => (Array.isArray(list) ? list.map((t) => {
      const rawName = t.taskName || '';
      const timeMatch = rawName.match(/\[(\d+(?:\.\d+)?)\s*HRS?\]/i);
      const estimatedHours = timeMatch ? parseFloat(timeMatch[1]) : null;
      const cleanedName = rawName.replace(/\[(\d+(?:\.\d+)?)\s*HRS?\]/i, '').replace(/\s*-\s*$/, '').trim();
      return {
        ...t,
        id: t.id || t.taskId || '',
        clientName: cleanedName,
        estimatedHours,
        completed: t.completed === true || t.completed === 1 || t.completed === '1' || t.completed === 'true',
        taskProfilePicUrl: `${API_URL}/api/bid/opportunity/profile/pic/${encodeURIComponent(userEmail || '')}`,
      };
    }) : []);

    const now = new Date();
    const currentWeekStart = new Date(now);
    const day = currentWeekStart.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    currentWeekStart.setHours(0, 0, 0, 0);
    currentWeekStart.setDate(currentWeekStart.getDate() + diffToMonday);

    const sortByDateAsc = (a, b) => parseDate(a.deadlineDate) - parseDate(b.deadlineDate);
    const sortByTimeThenDateWithCompletion = (a, b) => {
      const aCompleted = a.completed === true;
      const bCompleted = b.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ah = (typeof a.estimatedHours === 'number') ? a.estimatedHours : -1;
      const bh = (typeof b.estimatedHours === 'number') ? b.estimatedHours : -1;
      if (ah !== bh) return bh - ah;
      return sortByDateAsc(a, b);
    };

    const overdueOnlyPastWeeks = mapForCard(jsonData.overdue)
      .filter((t) => parseDate(t.deadlineDate) < currentWeekStart)
      .sort(sortByTimeThenDateWithCompletion);

    const thisWeekSorted = mapForCard(jsonData.thisWeek).sort(sortByTimeThenDateWithCompletion);
    const nextWeekSorted = mapForCard(jsonData.nextWeek).sort(sortByTimeThenDateWithCompletion);

    return { overdue: overdueOnlyPastWeeks, thisWeek: thisWeekSorted, nextWeek: nextWeekSorted };
  }, [dashboardPersonal?.tasks, API_URL, userEmail]);

  const twoWeekData = dashboardPersonal?.twoWeekData || null;
  const loading = Boolean(dashboardPersonal?.tasksLoading || dashboardPersonal?.twoWeekLoading);
  const error = dashboardPersonal?.tasksError || dashboardPersonal?.twoWeekError || null;

  const tasksPromiseRef = useRef(null);

  // Group "This week" tasks by weekday (Mon-Fri); completed sort to bottom within each day
  const thisWeekByDay = React.useMemo(() => {
    const groups = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    const parseDate = (dateString) => {
      if (!dateString) return new Date(0);
      const parts = dateString.split('/');
      return new Date(parts[2], parts[1] - 1, parts[0]);
    };
    const sortByCompletionThenDate = (a, b) => {
      const aCompleted = a.completed === true;
      const bCompleted = b.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      return parseDate(a.deadlineDate) - parseDate(b.deadlineDate);
    };
    thisWeek.forEach(t => {
      const d = parseDate(t.deadlineDate);
      let day = d.getDay(); // 0=Sun .. 6=Sat
      if (day === 0) day = 7; // treat Sunday as 7
      if (day > 5) day = 5; // map Sat/Sun to Friday bucket for display
      groups[day].push(t);
    });
    for (let i = 1; i <= 5; i++) groups[i].sort(sortByCompletionThenDate);
    return groups;
  }, [thisWeek]);

  // Prepare a full-width horizontal board model for this week (Mon-Fri) using start and due dates
  const thisWeekBoard = React.useMemo(() => {
    const parseDate = (dateString) => {
      if (!dateString) return null;
      const parts = (dateString || '').split('/');
      if (parts.length !== 3) return null;
      let yearNum = parseInt(parts[2], 10);
      if (parts[2].length === 2) {
        yearNum = yearNum >= 70 ? 1900 + yearNum : 2000 + yearNum;
      }
      return new Date(yearNum, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    };
    // Find Monday of current week
    const now = new Date();
    const monday = new Date(now);
    const day = monday.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(monday.getDate() + diffToMonday);
    const friday = new Date(monday); friday.setDate(monday.getDate() + 4);

    const toIndex = (d) => {
      if (!d) return 1;
      const clamped = new Date(Math.min(Math.max(d.getTime(), monday.getTime()), friday.getTime()));
      const diffDays = Math.floor((clamped - monday) / (1000 * 60 * 60 * 24));
      return diffDays + 1; // 1..5
    };
    const getStartDate = (t) => parseDate(
      t.startDate || t.start_date || t.start || t.startdate || t.plannedStartDate || t.planned_start_date
    );

    const sortByCompletionThenDue = (a, b) => {
      const aCompleted = a.completed === true;
      const bCompleted = b.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ad = parseDate(a.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    };

    const items = [...thisWeek].sort(sortByCompletionThenDue).map((t) => {
      const due = parseDate(t.deadlineDate) || friday;
      const startCandidate = getStartDate(t) || due; // if no start, show as a 1-day task on due day
      const startIdx = toIndex(startCandidate);
      const endIdx = Math.max(startIdx, toIndex(due));
      return { task: t, startIdx, endIdx };
    });
    // Order: column (startIdx) first so days with no other items don't have gaps,
    // then multi-day spans first, then incomplete before completed, then by due date
    items.sort((a, b) => {
      if (a.startIdx !== b.startIdx) return a.startIdx - b.startIdx;
      const aMulti = a.endIdx > a.startIdx;
      const bMulti = b.endIdx > b.startIdx;
      if (aMulti !== bMulti) return aMulti ? -1 : 1;
      const aCompleted = a.task.completed === true;
      const bCompleted = b.task.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ad = parseDate(a.task.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.task.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    });
    // Split into top multi-day layer and per-day single stacks
    const itemsMultiRaw = items.filter(i => i.endIdx > i.startIdx);
    const dayStacks = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    const daysWithSpan = { 1: false, 2: false, 3: false, 4: false, 5: false };
    items.forEach(i => { if (i.endIdx === i.startIdx) { dayStacks[i.startIdx].push(i); } });
    // Assign non-overlapping layers to multi-day spans (for stacked rows)
    const layered = [];
    const layerEnds = [];
    itemsMultiRaw.sort((a, b) => (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx) || a.startIdx - b.startIdx).forEach(it => {
      let placed = false;
      for (let l = 0; l < layerEnds.length; l++) {
        if (it.startIdx > layerEnds[l]) { it.layer = l + 1; layerEnds[l] = it.endIdx; layered.push(it); placed = true; break; }
      }
      if (!placed) { it.layer = layerEnds.length + 1; layerEnds.push(it.endIdx); layered.push(it); }
    });
    const maxLayer = layerEnds.length;
    // Mark days covered by multi-day spans
    layered.forEach(i => { for (let d = i.startIdx; d <= i.endIdx; d++) { if (daysWithSpan[d] !== undefined) daysWithSpan[d] = true; } });
    const sortDay = (arr) => arr.sort((a, b) => {
      const aCompleted = a.task.completed === true;
      const bCompleted = b.task.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ah = (typeof a.task.estimatedHours === 'number') ? a.task.estimatedHours : -1;
      const bh = (typeof b.task.estimatedHours === 'number') ? b.task.estimatedHours : -1;
      if (ah !== bh) return bh - ah; // most time first
      const ad = parseDate(a.task.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.task.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    });
    for (let d = 1; d <= 5; d++) sortDay(dayStacks[d]);

    // Build unified row model so spans and singles align per row
    const hasAnyByDay = { 1: false, 2: false, 3: false, 4: false, 5: false };
    for (let d = 1; d <= 5; d++) {
      hasAnyByDay[d] = dayStacks[d].length > 0 || layered.some(s => s.startIdx <= d && s.endIdx >= d);
    }
    const rows = [];
    const ensureRow = (r) => { if (!rows[r]) { rows[r] = { 1: null, 2: null, 3: null, 4: null, 5: null }; } };
    // Place spans first
    layered.forEach(span => {
      let r = 0;
      while (true) {
        ensureRow(r);
        let can = true;
        for (let d = span.startIdx; d <= span.endIdx; d++) { if (rows[r][d]) { can = false; break; } }
        if (can) {
          rows[r][span.startIdx] = { type: 'span', task: span.task, colSpan: (span.endIdx - span.startIdx + 1) };
          for (let d = span.startIdx + 1; d <= span.endIdx; d++) rows[r][d] = { type: 'blocked' };
          break;
        }
        r++;
      }
    });
    // Place single-day tasks
    const ptr = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let placedAny = true;
    while (placedAny) {
      placedAny = false;
      for (let d = 1; d <= 5; d++) {
        if (ptr[d] >= dayStacks[d].length) continue;
        let r = 0;
        while (true) {
          ensureRow(r);
          if (!rows[r][d]) { rows[r][d] = { type: 'single', task: dayStacks[d][ptr[d]].task || dayStacks[d][ptr[d]] }; ptr[d]++; placedAny = true; break; }
          r++;
        }
      }
    }

    // Remove rows that have only blocked/empty cells and order rows by earliest active day
    const compactRows = rows
      .filter(r => [1, 2, 3, 4, 5].some(d => r[d] && r[d].type && r[d].type !== 'blocked'))
      .sort((a, b) => {
        const aFirst = [1, 2, 3, 4, 5].find(d => a[d] && a[d].type && a[d].type !== 'blocked') || 99;
        const bFirst = [1, 2, 3, 4, 5].find(d => b[d] && b[d].type && b[d].type !== 'blocked') || 99;
        return aFirst - bFirst;
      });

    return { monday, friday, itemsMulti: layered, dayStacks, daysWithSpan, itemsAll: items, maxLayer, rows: compactRows, hasAnyByDay };
  }, [thisWeek]);

  // Prepare a similar board for next week (Mon-Fri)
  const nextWeekBoard = React.useMemo(() => {
    const parseDate = (dateString) => {
      if (!dateString) return null;
      const parts = (dateString || '').split('/');
      if (parts.length !== 3) return null;
      let yearNum = parseInt(parts[2], 10);
      if (parts[2].length === 2) {
        yearNum = yearNum >= 70 ? 1900 + yearNum : 2000 + yearNum;
      }
      return new Date(yearNum, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    };
    // Find Monday of next week
    const now = new Date();
    const mondayThis = new Date(now);
    const day = mondayThis.getDay();
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    mondayThis.setHours(0, 0, 0, 0);
    mondayThis.setDate(mondayThis.getDate() + diffToMonday);
    const monday = new Date(mondayThis);
    monday.setDate(mondayThis.getDate() + 7);
    const friday = new Date(monday); friday.setDate(monday.getDate() + 4);

    const toIndex = (d) => {
      if (!d) return 1;
      const clamped = new Date(Math.min(Math.max(d.getTime(), monday.getTime()), friday.getTime()));
      const diffDays = Math.floor((clamped - monday) / (1000 * 60 * 60 * 24));
      return diffDays + 1; // 1..5
    };
    const getStartDate = (t) => parseDate(
      t.startDate || t.start_date || t.start || t.startdate || t.plannedStartDate || t.planned_start_date
    );

    const sortByCompletionThenDue = (a, b) => {
      const aCompleted = a.completed === true;
      const bCompleted = b.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ad = parseDate(a.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    };

    const items = [...nextWeek].sort(sortByCompletionThenDue).map((t) => {
      const due = parseDate(t.deadlineDate) || friday;
      const startCandidate = getStartDate(t) || due;
      const startIdx = toIndex(startCandidate);
      const endIdx = Math.max(startIdx, toIndex(due));
      return { task: t, startIdx, endIdx };
    });
    items.sort((a, b) => {
      if (a.startIdx !== b.startIdx) return a.startIdx - b.startIdx;
      const aMulti = a.endIdx > a.startIdx;
      const bMulti = b.endIdx > b.startIdx;
      if (aMulti !== bMulti) return aMulti ? -1 : 1;
      const aCompleted = a.task.completed === true;
      const bCompleted = b.task.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ad = parseDate(a.task.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.task.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    });
    const itemsMultiRaw = items.filter(i => i.endIdx > i.startIdx);
    const dayStacks = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    const daysWithSpan = { 1: false, 2: false, 3: false, 4: false, 5: false };
    items.forEach(i => { if (i.endIdx === i.startIdx) { dayStacks[i.startIdx].push(i); } });
    // Layering for next week
    const layered = [];
    const layerEnds = [];
    itemsMultiRaw.sort((a, b) => (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx) || a.startIdx - b.startIdx).forEach(it => {
      let placed = false;
      for (let l = 0; l < layerEnds.length; l++) {
        if (it.startIdx > layerEnds[l]) { it.layer = l + 1; layerEnds[l] = it.endIdx; layered.push(it); placed = true; break; }
      }
      if (!placed) { it.layer = layerEnds.length + 1; layerEnds.push(it.endIdx); layered.push(it); }
    });
    const maxLayer = layerEnds.length;
    layered.forEach(i => { for (let d = i.startIdx; d <= i.endIdx; d++) { if (daysWithSpan[d] !== undefined) daysWithSpan[d] = true; } });
    const sortDay = (arr) => arr.sort((a, b) => {
      const aCompleted = a.task.completed === true;
      const bCompleted = b.task.completed === true;
      if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
      const ad = parseDate(a.task.deadlineDate) || new Date(8640000000000000);
      const bd = parseDate(b.task.deadlineDate) || new Date(8640000000000000);
      return ad - bd;
    });
    for (let d = 1; d <= 5; d++) sortDay(dayStacks[d]);

    const hasAnyByDay = { 1: false, 2: false, 3: false, 4: false, 5: false };
    for (let d = 1; d <= 5; d++) {
      hasAnyByDay[d] = dayStacks[d].length > 0 || layered.some(s => s.startIdx <= d && s.endIdx >= d);
    }
    const rows = [];
    const ensureRow = (r) => { if (!rows[r]) { rows[r] = { 1: null, 2: null, 3: null, 4: null, 5: null }; } };
    layered.forEach(span => {
      let r = 0;
      while (true) {
        ensureRow(r);
        let can = true;
        for (let d = span.startIdx; d <= span.endIdx; d++) { if (rows[r][d]) { can = false; break; } }
        if (can) {
          rows[r][span.startIdx] = { type: 'span', task: span.task, colSpan: (span.endIdx - span.startIdx + 1) };
          for (let d = span.startIdx + 1; d <= span.endIdx; d++) rows[r][d] = { type: 'blocked' };
          break;
        }
        r++;
      }
    });
    const ptr = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let placedAny = true;
    while (placedAny) {
      placedAny = false;
      for (let d = 1; d <= 5; d++) {
        if (ptr[d] >= dayStacks[d].length) continue;
        let r = 0;
        while (true) {
          ensureRow(r);
          if (!rows[r][d]) { rows[r][d] = { type: 'single', task: dayStacks[d][ptr[d]].task || dayStacks[d][ptr[d]] }; ptr[d]++; placedAny = true; break; }
          r++;
        }
      }
    }
    const compactRows = rows
      .filter(r => [1, 2, 3, 4, 5].some(d => r[d] && r[d].type && r[d].type !== 'blocked'))
      .sort((a, b) => {
        const aFirst = [1, 2, 3, 4, 5].find(d => a[d] && a[d].type && a[d].type !== 'blocked') || 99;
        const bFirst = [1, 2, 3, 4, 5].find(d => b[d] && b[d].type && b[d].type !== 'blocked') || 99;
        return aFirst - bFirst;
      });
    return { monday, friday, itemsMulti: layered, dayStacks, daysWithSpan, itemsAll: items, maxLayer, rows: compactRows, hasAnyByDay };
  }, [nextWeek]);

  useEffect(() => {
    if (!userEmail) return;

    tasksPromiseRef.current?.abort?.();
    tasksPromiseRef.current = dispatch(fetchDashboardPersonalTasks({ userEmail }));
    const twoWeekPromise = dispatch(fetchDashboardCalendarTwoWeeks({ userEmail }));

    // Load gold links (existing slice thunk)
    dispatch(fetchPersonalLinks());

    // Listen for refresh signal from TaskCard updates
    const refreshListener = () => {
      tasksPromiseRef.current?.abort?.();
      tasksPromiseRef.current = dispatch(fetchDashboardPersonalTasks({ userEmail }));
    };
    window.addEventListener('personalTasks:refresh', refreshListener);

    return () => {
      window.removeEventListener('personalTasks:refresh', refreshListener);
      tasksPromiseRef.current?.abort?.();
      twoWeekPromise?.abort?.();
    };
  }, [dispatch, userEmail]);

  return (
    <div className={styles.opportunitiesContainer}>
      <div className={styles.personalHeader}>
        <div className={styles.pageTitle}>Personal</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginLeft: 'auto', justifyContent: 'flex-end' }}>
            <button className={styles.personalBtn} onClick={() => setShowGenericTriage(true)}>BOOK TRIAGE</button>
            <button className={styles.personalBtn} onClick={() => setShowITForm(true)}>IT TICKET</button>
            <button className={styles.personalBtn} onClick={() => setShowTownhall(true)}>TOWNHALL TOPIC</button>
            <button className={styles.personalBtn} onClick={() => setShowTdg(true)}>TDG TOPIC</button>
            <button className={styles.personalBtn} onClick={() => setShowStandardsForm(true)}>STANDARDS & TEMPLATES</button>
          </div>
          {(personalLinksState?.links || []).length > 0 && (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginLeft: 'auto' }}>
                {(personalLinksState?.links || []).map(link => (
                  <button
                    key={link.id}
                    className={`${styles.personalBtn} ${styles.personalBtnGold}`}
                    onClick={() => { if (link.url) window.open(link.url, '_blank'); }}
                    title={link.url || ''}
                  >
                    {link.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }} aria-live="polite">
          <CircularProgress size={48} />
        </div>
      ) : (
        <Grid container spacing={1} sx={{ mt: 5 }}>
          {/* Full-width board with side columns: Overdue | Mon-Fri | Next week */}
          <Grid item xs={12}>
            <div className={styles.oppHead}>This week</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(() => {
                const colGap = 12;
                return (
                  <>
                    {/* Header: Overdue | Mon-Fri | Next week (flex) */}
                    <div style={{ display: 'flex', gap: colGap, alignItems: 'flex-end' }}>
                      <div style={{ display: 'flex', flex: 7, gap: colGap }}>
                        {['Overdue', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Next week'].map(d => (
                          <div key={d} style={{ flex: 1, fontWeight: 600, paddingLeft: 4 }}>{d}</div>
                        ))}
                      </div>
                    </div>

                    {/* Content row: Overdue list | multi-day spans over Mon-Fri */}
                    <div style={{ display: 'flex', gap: colGap }}>

                      {/* Middle + Next week columns */}
                      <div style={{ display: 'flex', gap: colGap, flex: 7 }}>
                        {/* Left: Overdue single column */}
                        <div style={{ flex: 1 }}>
                          <div className={styles.scrollableColumn}>
                            {overdue && overdue.length ? (
                              overdue.map(task => (
                                <TaskCard
                                  key={`nw-col-${task.id}`}
                                  task={task}
                                  setupUploadModal={() => { }}
                                  personalMode={true}
                                  boardFullWidth={true}
                                  compact={true}
                                  truncateTitleOneLine={true}
                                  titleMaxWidth={168}
                                  largeAvatar={true}
                                />
                              ))
                            ) : (
                              <Card className={styles.Card} style={{ width: '100%' }}>
                                <CardContent className={styles.CardContent}>
                                  <Typography gutterBottom variant="h5" component="div" className={styles.cardTitle} style={{ flex: 1, wordWrap: 'break-word' }}>No Tasks</Typography>
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        </div>
                        {/* Middle: Mon–Fri rows */}
                        <div style={{ flex: 5, display: 'flex', flexDirection: 'column', gap: colGap }}>
                          {thisWeekBoard.rows.map((row, rowIdx) => (
                            <div key={`row-${rowIdx}`} style={{ display: 'flex', gap: colGap }}>
                              {[1, 2, 3, 4, 5].map((d) => {
                                const cell = row[d];
                                if (!cell) {
                                  return (
                                    <div key={`cell-empty-${rowIdx}-${d}`} style={{ flex: 1 }}>
                                      {rowIdx === 0 && !thisWeekBoard.hasAnyByDay[d] ? (
                                        <Card className={styles.Card} style={{ width: '100%' }}>
                                          <CardContent className={styles.CardContent}>
                                            <Typography gutterBottom variant="h5" component="div" className={styles.cardTitle} style={{ flex: 1, wordWrap: 'break-word' }}>No Tasks</Typography>
                                          </CardContent>
                                        </Card>
                                      ) : null}
                                    </div>
                                  );
                                }
                                if (cell.type === 'blocked') {
                                  // This column is covered by a span starting earlier; skip rendering to let the span occupy the width
                                  return null;
                                }
                                if (cell.type === 'span') {
                                  return (
                                    <div key={`cell-span-${rowIdx}-${d}`} style={{ flex: cell.colSpan, display: 'flex' }}>
                                      <TaskCard task={cell.task} setupUploadModal={() => { }} personalMode={true} boardFullWidth={true} compact={true} largeAvatar={true} />
                                    </div>
                                  );
                                }
                                return (
                                  <div key={`cell-single-${rowIdx}-${d}`} style={{ flex: 1 }}>
                                    <TaskCard task={cell.task} setupUploadModal={() => { }} personalMode={true} boardFullWidth={true} compact={true} truncateTitleOneLine={true} titleMaxWidth={168} largeAvatar={true} />
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        {/* Right: Next week single column */}
                        <div style={{ flex: 1 }}>
                          <div className={styles.scrollableColumn}>
                            {nextWeek && nextWeek.length ? (
                              nextWeek.map(task => (
                                <div key={`cell-single-$${task.id}`} style={{ flex: 1 }}>
                                  <TaskCard task={task} setupUploadModal={() => { }} personalMode={true} boardFullWidth={true} compact={true} truncateTitleOneLine={true} titleMaxWidth={168} largeAvatar={true} />
                                </div>
                              ))
                            ) : (
                              <Card className={styles.Card} style={{ width: '100%' }}>
                                <CardContent className={styles.CardContent}>
                                  <Typography gutterBottom variant="h5" component="div" className={styles.cardTitle} style={{ flex: 1, wordWrap: 'break-word' }}>No Tasks</Typography>
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </Grid>

          {/* Full-width calendar under the columns */}
          <Grid item xs={12}>
            <div className={styles.oppHead} style={{ marginTop: 18, marginBottom: 12 }}>Company Leave Calendar (2 Weeks)</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }} className={styles.calendarFullView}>
                {twoWeekData ? (
                  <TwoWeekCalendar resourceData={twoWeekData} hideKeys={true} />
                ) : (
                  <Card className={styles.Card}><CardContent className={styles.CardContent}><Typography
                    gutterBottom
                    variant="h5"
                    component="div"
                    className={styles.cardTitle}
                    style={{ flex: 1, wordWrap: "break-word" }}
                  >
                    Loading calendar...
                  </Typography></CardContent></Card>
                )}
              </div>
            </div>
          </Grid>

          {showGenericTriage && (
            <TriageBookingModal
              isVisible={showGenericTriage}
              onClose={(created) => { setShowGenericTriage(false); if (created) { /* optionally refresh calendar or tasks */ } }}
              defaultName={account?.name}
              defaultEmail={account?.username}
            />
          )}

          {showITForm && (
            <ITForm
              onClose={() => setShowITForm(false)}
              onSubmit={(data) => {
                // TODO: send to backend or dispatch an action
                console.log('IT Ticket submitted', data);
                setShowITForm(false);
              }}
            />
          )}

          {showTownhall && (
            <TownhallForm
              onClose={() => setShowTownhall(false)}
              onSubmit={(data) => {
                console.log('Townhall topic submitted', data);
                setShowTownhall(false);
              }}
            />
          )}

          {showTdg && (
            <TdgForm
              onClose={() => setShowTdg(false)}
              onSubmit={(data) => {
                console.log('TDG topic submitted', data);
                setShowTdg(false);
              }}
            />
          )}

          {showStandardsForm && (
            <StandardsAndTemplatesForm
              onClose={() => setShowStandardsForm(false)}
              onSubmit={(data) => {
                console.log('Standards and Templates ticket submitted', data);
                setShowStandardsForm(false);
              }}
              defaultEmail={account?.username}
            />
          )}


        </Grid>
      )}
    </div>
  );
}

export default Personal;


